import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Minimize2, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { chatbotResponses, companyInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';

const Chatbot = () => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showSMSAlert, setShowSMSAlert] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '' });
  const [smsData, setSmsData] = useState({ phone: '', name: '' });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = chatbotResponses.greeting[Math.floor(Math.random() * chatbotResponses.greeting.length)];
      setMessages([{
        id: 1,
        text: greeting,
        sender: 'bot',
        timestamp: new Date()
      }]);
    }
  }, [isOpen]);

  const isComplexQuery = (message) => {
    const complexKeywords = [
      'customize', 'integration', 'api', 'database', 'complex', 
      'advanced', 'enterprise', 'migration', 'specific requirement',
      'custom feature', 'blockchain', 'ai integration', 'machine learning'
    ];
    
    const hasMultipleQuestions = (message.match(/\?/g) || []).length > 2;
    const isLongMessage = message.split(' ').length > 30;
    const hasComplexKeyword = complexKeywords.some(keyword => message.includes(keyword));
    
    return hasMultipleQuestions || isLongMessage || hasComplexKeyword;
  };

  const getBotResponse = (userMessage) => {
    const message = userMessage.toLowerCase();
    
    // Check if query is complex
    if (isComplexQuery(message)) {
      setShowSMSAlert(true);
      return chatbotResponses.complex;
    }
    
    if (message.includes('service') || message.includes('what do you')) {
      return chatbotResponses.services;
    } else if (message.includes('price') || message.includes('cost') || message.includes('how much')) {
      return chatbotResponses.pricing;
    } else if (message.includes('contact') || message.includes('email') || message.includes('phone')) {
      return chatbotResponses.contact;
    } else if (message.includes('team') || message.includes('who are')) {
      return chatbotResponses.team;
    } else if (message.includes('seo') || message.includes('search engine') || message.includes('ranking')) {
      return chatbotResponses.seo;
    } else if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
      return chatbotResponses.greeting[Math.floor(Math.random() * chatbotResponses.greeting.length)];
    } else if (message.includes('lead') || message.includes('quote') || message.includes('interested')) {
      setShowLeadForm(true);
      return "Great! I'd love to help you get started. Please fill in your details below and our team will reach out to you shortly.";
    }
    
    return chatbotResponses.default;
  };

  const handleSendMessage = () => {
    if (inputMessage.trim() === '') return;

    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages([...messages, userMessage]);
    setInputMessage('');

    setTimeout(() => {
      const botResponse = {
        id: messages.length + 2,
        text: getBotResponse(inputMessage),
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }, 1000);
  };

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    
    const leads = JSON.parse(localStorage.getItem('crs_leads') || '[]');
    leads.push({ ...leadData, timestamp: new Date().toISOString() });
    localStorage.setItem('crs_leads', JSON.stringify(leads));

    const confirmMessage = {
      id: messages.length + 1,
      text: `Thank you, ${leadData.name}! We've received your information. Our team will contact you at ${leadData.email} or ${leadData.phone} within 24 hours. Looking forward to working with you!`,
      sender: 'bot',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, confirmMessage]);
    setShowLeadForm(false);
    setLeadData({ name: '', email: '', phone: '' });
  };

  const handleSMSAlertSubmit = (e) => {
    e.preventDefault();
    
    // Store SMS alert request
    const smsAlerts = JSON.parse(localStorage.getItem('crs_sms_alerts') || '[]');
    smsAlerts.push({ 
      ...smsData, 
      timestamp: new Date().toISOString(),
      ownerPhone: companyInfo.phone
    });
    localStorage.setItem('crs_sms_alerts', JSON.stringify(smsAlerts));

    toast({
      title: "SMS Alert Requested!",
      description: `Our team will call you at ${smsData.phone} shortly to discuss your requirements.`,
    });

    const confirmMessage = {
      id: messages.length + 1,
      text: `Perfect! I've notified our expert team. They will call you at ${smsData.phone} within the next few hours to discuss your specific requirements in detail. Thank you for your patience!`,
      sender: 'bot',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, confirmMessage]);
    setShowSMSAlert(false);
    setSmsData({ phone: '', name: '' });
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-cyan-500 to-teal-600 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform duration-300 z-50 group"
        >
          <MessageCircle className="w-8 h-8 text-white" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full animate-pulse"></span>
          <div className="absolute right-20 bg-gray-900 text-white px-4 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
            Chat with us!
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-6 right-6 w-96 h-[600px] shadow-2xl z-50 flex flex-col border-2 border-cyan-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-teal-600 text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">CRS Tech Assistant</h3>
                <p className="text-xs text-cyan-100">Online - Ready to help!</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setMessages([]);
                  setShowLeadForm(false);
                  setShowSMSAlert(false);
                }}
                className="hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <CardContent className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    message.sender === 'user'
                      ? 'bg-gradient-to-r from-cyan-500 to-teal-600 text-white rounded-br-none'
                      : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none shadow-md'
                  }`}
                >
                  <p className="text-sm leading-relaxed">{message.text}</p>
                  <p className={`text-xs mt-1 ${
                    message.sender === 'user' ? 'text-cyan-100' : 'text-gray-400'
                  }`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}

            {/* SMS Alert Form */}
            {showSMSAlert && (
              <div className="bg-orange-50 p-4 rounded-2xl border-2 border-orange-300 shadow-lg">
                <div className="flex items-center space-x-2 mb-3">
                  <Phone className="w-5 h-5 text-orange-600" />
                  <h4 className="font-bold text-gray-900">Request Callback</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Your query needs expert attention. Please provide your phone number and our team will call you back.
                </p>
                <form onSubmit={handleSMSAlertSubmit} className="space-y-3">
                  <Input
                    type="text"
                    placeholder="Your Name"
                    required
                    value={smsData.name}
                    onChange={(e) => setSmsData({ ...smsData, name: e.target.value })}
                    className="border-2 border-orange-200 focus:border-orange-500"
                  />
                  <Input
                    type="tel"
                    placeholder="Your Phone Number"
                    required
                    value={smsData.phone}
                    onChange={(e) => setSmsData({ ...smsData, phone: e.target.value })}
                    className="border-2 border-orange-200 focus:border-orange-500"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                  >
                    Request Callback
                  </Button>
                </form>
              </div>
            )}

            {/* Lead Form */}
            {showLeadForm && (
              <div className="bg-white p-4 rounded-2xl border-2 border-cyan-200 shadow-lg">
                <h4 className="font-bold text-gray-900 mb-3">Your Information</h4>
                <form onSubmit={handleLeadSubmit} className="space-y-3">
                  <Input
                    type="text"
                    placeholder="Your Name"
                    required
                    value={leadData.name}
                    onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                    className="border-2 border-gray-200 focus:border-cyan-500"
                  />
                  <Input
                    type="email"
                    placeholder="Your Email"
                    required
                    value={leadData.email}
                    onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                    className="border-2 border-gray-200 focus:border-cyan-500"
                  />
                  <Input
                    type="tel"
                    placeholder="Your Phone"
                    required
                    value={leadData.phone}
                    onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })}
                    className="border-2 border-gray-200 focus:border-cyan-500"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white"
                  >
                    Submit
                  </Button>
                </form>
              </div>
            )}

            <div ref={messagesEndRef} />
          </CardContent>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
            <div className="flex space-x-2">
              <Input
                type="text"
                placeholder="Type your message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                className="flex-1 border-2 border-gray-200 focus:border-cyan-500"
              />
              <Button
                onClick={handleSendMessage}
                className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">Ask about services, pricing, or request a quote!</p>
          </div>
        </Card>
      )}
    </>
  );
};

export default Chatbot;